import type { ObjetoTabletop, TipoObjetoTabletop } from './tipos'

/**
 * Parâmetros editáveis de cada tipo de objeto: as medidas que mudam a forma
 * dele (e, com ela, a posição dos pontos de ancoragem e a altura do terreno).
 *
 * Fica separado de `ancoragem.ts` e `terreno.ts` porque os dois dependem destas
 * definições; assim nenhum precisa importar o outro.
 */

export interface DefinicaoParametro {
  readonly chave: string
  readonly rotulo: string
  /** Vazia nos parâmetros sem unidade (liga/desliga). */
  readonly unidade: 'm' | '°' | 'kg' | 'kN' | ''
  /** Nomes das opções de um parâmetro de escolha (0, 1, 2…), na ordem. */
  readonly opcoes?: readonly string[]
  readonly padrao: number
  readonly min: number
  readonly max: number
  readonly passo: number
}

/**
 * Os valores padrão são ponto de partida didático, não recomendação de uso.
 * Talude e barranco têm a mesma forma (rampa com topo plano); o que muda é a
 * faixa de inclinação: o talude é suave, o barranco é quase vertical.
 */
const DEFINICOES: Readonly<Partial<Record<TipoObjetoTabletop, readonly DefinicaoParametro[]>>> = {
  escada: [
    { chave: 'anguloGraus', rotulo: 'Ângulo com o chão', unidade: '°', padrao: 75, min: 10, max: 90, passo: 1 },
    { chave: 'comprimentoM', rotulo: 'Comprimento', unidade: 'm', padrao: 6, min: 2, max: 12, passo: 0.1 },
  ],
  toco: [
    { chave: 'alturaM', rotulo: 'Altura', unidade: 'm', padrao: 0.5, min: 0.1, max: 1.5, passo: 0.05 },
    { chave: 'diametroM', rotulo: 'Diâmetro', unidade: 'm', padrao: 0.4, min: 0.15, max: 1.2, passo: 0.05 },
  ],
  talude: [
    { chave: 'anguloGraus', rotulo: 'Inclinação', unidade: '°', padrao: 30, min: 10, max: 45, passo: 1 },
    { chave: 'alturaM', rotulo: 'Altura', unidade: 'm', padrao: 3, min: 0.5, max: 8, passo: 0.1 },
    { chave: 'larguraM', rotulo: 'Largura', unidade: 'm', padrao: 8, min: 2, max: 30, passo: 0.5 },
    { chave: 'topoM', rotulo: 'Topo plano', unidade: 'm', padrao: 3, min: 0, max: 20, passo: 0.5 },
  ],
  barranco: [
    { chave: 'anguloGraus', rotulo: 'Inclinação', unidade: '°', padrao: 75, min: 45, max: 90, passo: 1 },
    { chave: 'alturaM', rotulo: 'Altura', unidade: 'm', padrao: 4, min: 1, max: 12, passo: 0.1 },
    { chave: 'larguraM', rotulo: 'Largura', unidade: 'm', padrao: 8, min: 2, max: 30, passo: 0.5 },
    { chave: 'topoM', rotulo: 'Topo plano', unidade: 'm', padrao: 3, min: 0, max: 20, passo: 0.5 },
    { chave: 'pistaNoTopo', rotulo: 'Pista de asfalto no topo (0 não, 1 sim)', unidade: '', padrao: 0, min: 0, max: 1, passo: 1 },
  ],
  canal: [
    { chave: 'comprimentoM', rotulo: 'Comprimento', unidade: 'm', padrao: 40, min: 10, max: 80, passo: 1 },
    { chave: 'larguraLeitoM', rotulo: 'Largura do leito', unidade: 'm', padrao: 10, min: 3, max: 30, passo: 0.5 },
    { chave: 'alturaParedeM', rotulo: 'Altura das paredes', unidade: 'm', padrao: 5, min: 1.5, max: 12, passo: 0.1 },
    { chave: 'banquetaLarguraM', rotulo: 'Largura da banqueta', unidade: 'm', padrao: 1.5, min: 0, max: 4, passo: 0.1 },
    { chave: 'banquetaAlturaM', rotulo: 'Altura da banqueta', unidade: 'm', padrao: 1.2, min: 0.3, max: 3, passo: 0.1 },
    { chave: 'inclinacaoMargemGraus', rotulo: 'Inclinação da margem oposta', unidade: '°', padrao: 90, min: 30, max: 90, passo: 1 },
    { chave: 'muretaAlturaM', rotulo: 'Altura da mureta', unidade: 'm', padrao: 1, min: 0, max: 1.5, passo: 0.1 },
    { chave: 'nivelAguaM', rotulo: 'Nível da água', unidade: 'm', padrao: 0.5, min: 0, max: 4, passo: 0.1 },
    { chave: 'correnteza', rotulo: 'Setas de correnteza (0 não, 1 sim)', unidade: '', padrao: 1, min: 0, max: 1, passo: 1 },
  ],
  ponte: [
    { chave: 'vaoM', rotulo: 'Vão', unidade: 'm', padrao: 11.5, min: 4, max: 30, passo: 0.5 },
    { chave: 'larguraM', rotulo: 'Largura', unidade: 'm', padrao: 7, min: 3, max: 15, passo: 0.5 },
    { chave: 'espessuraM', rotulo: 'Altura da laje e das vigas', unidade: 'm', padrao: 0.9, min: 0.3, max: 2, passo: 0.1 },
    { chave: 'guardaCorpoM', rotulo: 'Altura do guarda-corpo', unidade: 'm', padrao: 1.1, min: 0, max: 1.6, passo: 0.1 },
    { chave: 'pilarAlturaM', rotulo: 'Pilar central (0 sem pilar)', unidade: 'm', padrao: 0, min: 0, max: 10, passo: 0.5 },
  ],
  trabalhador: [
    { chave: 'comprimentoTalabarteM', rotulo: 'Comprimento do talabarte', unidade: 'm', padrao: 1.8, min: 0.5, max: 2.5, passo: 0.1 },
    { chave: 'absorvedorAberturaM', rotulo: 'Abertura do absorvedor de energia', unidade: 'm', padrao: 1, min: 0, max: 1.75, passo: 0.05 },
    { chave: 'engateAosPesM', rotulo: 'Distância do engate dorsal aos pés', unidade: 'm', padrao: 1.45, min: 1.2, max: 1.8, passo: 0.05 },
    { chave: 'margemSegurancaM', rotulo: 'Margem de segurança', unidade: 'm', padrao: 1, min: 0, max: 3, passo: 0.1 },
    { chave: 'massaKg', rotulo: 'Massa com equipamento', unidade: 'kg', padrao: 100, min: 40, max: 200, passo: 5 },
    {
      chave: 'materialTalabarte',
      rotulo: 'Material do talabarte (0 fita de poliamida, 1 fita de Dyneema, 2 corda semiestática, 3 corda dinâmica)',
      unidade: '',
      opcoes: ['fita de poliamida', 'fita de Dyneema', 'corda semiestática', 'corda dinâmica'],
      padrao: 0,
      min: 0,
      max: 3,
      passo: 1,
    },
    { chave: 'forcaAbsorvedorKN', rotulo: 'Força de frenagem do absorvedor', unidade: 'kN', padrao: 4, min: 2, max: 6, passo: 0.5 },
    {
      chave: 'redutorForca',
      rotulo: 'Redutor de força (0 absorvedor de energia, 1 Prusik na corda, 2 freio dinâmico)',
      unidade: '',
      opcoes: ['absorvedor de energia', 'Prusik na corda', 'freio dinâmico'],
      padrao: 0,
      min: 0,
      max: 2,
      passo: 1,
    },
    {
      chave: 'dobraNoAlto',
      rotulo: 'Ancoragem (0 talabarte direto, 1 corda por polia no alto, 2 corda por mosquetão no alto)',
      unidade: '',
      opcoes: ['talabarte direto', 'corda por polia', 'corda por mosquetão'],
      padrao: 0,
      min: 0,
      max: 2,
      passo: 1,
    },
    { chave: 'sentadoNaCadeirinha', rotulo: 'Postura (0 em pé, 1 sentado na cadeirinha, acesso por corda)', unidade: '', padrao: 0, min: 0, max: 1, passo: 1 },
    { chave: 'cargaRupturaKN', rotulo: 'Carga de ruptura do talabarte', unidade: 'kN', padrao: 22, min: 2, max: 30, passo: 1 },
    { chave: 'fitaDanificada', rotulo: 'Fita danificada / aresta viva (0 íntegra, 1 danificada - rompe a 4 kN)', unidade: '', padrao: 0, min: 0, max: 1, passo: 1 },
  ],
  torre: [
    { chave: 'alturaM', rotulo: 'Altura', unidade: 'm', padrao: 12, min: 1, max: 40, passo: 0.5 },
    { chave: 'larguraM', rotulo: 'Largura (lado)', unidade: 'm', padrao: 3, min: 0.4, max: 8, passo: 0.1 },
    { chave: 'espacoPatamarM', rotulo: 'Patamar a cada', unidade: 'm', padrao: 3, min: 0, max: 10, passo: 0.5 },
  ],
  descensor: [
    { chave: 'tipoDescensor', rotulo: 'Tipo (0 oito, 1 autoblocante tipo ID)', unidade: '', padrao: 0, min: 0, max: 1, passo: 1 },
  ],
  protetorCorda: [
    { chave: 'comprimentoM', rotulo: 'Comprimento', unidade: 'm', padrao: 0.6, min: 0.3, max: 1.5, passo: 0.05 },
  ],
  envolvimento: [
    { chave: 'tipoEnvolvimento', rotulo: 'Tipo (0 cesta, 1 enforcado, 2 três voltas e duas pegadas)', unidade: '', padrao: 0, min: 0, max: 2, passo: 1 },
    { chave: 'diametroM', rotulo: 'Diâmetro do tronco', unidade: 'm', padrao: 0.4, min: 0.05, max: 1.5, passo: 0.01 },
    { chave: 'distanciaM', rotulo: 'Do centro do tronco ao mosquetão', unidade: 'm', padrao: 0.7, min: 0.1, max: 2.5, passo: 0.05 },
  ],
  sistemaVM: [
    { chave: 'razao', rotulo: 'Vantagem (n:1)', unidade: '', padrao: 3, min: 2, max: 6, passo: 1 },
    { chave: 'cursoM', rotulo: 'Distância entre os blocos', unidade: 'm', padrao: 2, min: 0.8, max: 6, passo: 0.1 },
    { chave: 'fusivel', rotulo: 'Fusível ou trava', unidade: '', opcoes: ['sem fusível', 'Prusik', 'fusível clássico', 'freio oito (passagem esportiva)', 'freio oito (passagem travada)'], padrao: 0, min: 0, max: 4, passo: 1 },
  ],
  cisterna: [
    { chave: 'diametroM', rotulo: 'Diâmetro', unidade: 'm', padrao: 2.4, min: 0.8, max: 6, passo: 0.1 },
    { chave: 'profundidadeM', rotulo: 'Profundidade', unidade: 'm', padrao: 6, min: 1.5, max: 15, passo: 0.5 },
    { chave: 'comTampa', rotulo: 'Tampa (0 aberta, 1 fechada)', unidade: '', padrao: 0, min: 0, max: 1, passo: 1 },
    { chave: 'meiaAberta', rotulo: 'Corte: metade aberta para ver dentro (0 não, 1 sim)', unidade: '', padrao: 1, min: 0, max: 1, passo: 1 },
    { chave: 'paredesTransparentes', rotulo: 'Paredes transparentes, sem o corte (0 não, 1 sim)', unidade: '', padrao: 1, min: 0, max: 1, passo: 1 },
  ],
  vala: [
    { chave: 'profundidadeM', rotulo: 'Profundidade', unidade: 'm', padrao: 2, min: 0.5, max: 5, passo: 0.1 },
    { chave: 'larguraM', rotulo: 'Largura do fundo', unidade: 'm', padrao: 1.5, min: 0.5, max: 6, passo: 0.1 },
    { chave: 'comprimentoM', rotulo: 'Comprimento', unidade: 'm', padrao: 10, min: 2, max: 40, passo: 0.5 },
  ],
  cavalo: [
    {
      chave: 'postura',
      rotulo: 'Postura (0 em pé, 1 deitado/caído)',
      unidade: '',
      opcoes: ['em pé', 'deitado/caído'],
      padrao: 0,
      min: 0,
      max: 1,
      passo: 1,
    },
    { chave: 'pesoKg', rotulo: 'Peso do animal', unidade: 'kg', padrao: 500, min: 200, max: 1000, passo: 25 },
  ],
  vaca: [
    {
      chave: 'postura',
      rotulo: 'Postura (0 em pé, 1 deitado/caído)',
      unidade: '',
      opcoes: ['em pé', 'deitado/caído'],
      padrao: 0,
      min: 0,
      max: 1,
      passo: 1,
    },
    { chave: 'pesoKg', rotulo: 'Peso do animal', unidade: 'kg', padrao: 500, min: 200, max: 1000, passo: 25 },
  ],
  estaca: [
    {
      chave: 'configuracao',
      rotulo: 'Configuração (0 simples, 1 tandem 1-1, 2 linha 1-1-1)',
      unidade: '',
      opcoes: ['simples (1 estaca)', 'tandem (1-1)', 'linha (1-1-1 com molinete)'],
      padrao: 0,
      min: 0,
      max: 2,
      passo: 1,
    },
    { chave: 'inclinacaoGraus', rotulo: 'Inclinação contra a força', unidade: '°', padrao: 60, min: 45, max: 90, passo: 5 },
  ],
}

const SEM_DEFINICOES: readonly DefinicaoParametro[] = []

export function definicoesDeParametros(tipo: TipoObjetoTabletop): readonly DefinicaoParametro[] {
  return DEFINICOES[tipo] ?? SEM_DEFINICOES
}

const prender = (valor: number, min: number, max: number) => Math.min(max, Math.max(min, valor))

/** Prende o valor ao intervalo do parâmetro; `undefined` se o tipo não tem esse parâmetro. */
export function clamparParametro(tipo: TipoObjetoTabletop, chave: string, valor: number): number | undefined {
  const definicao = definicoesDeParametros(tipo).find((d) => d.chave === chave)
  if (!definicao) return undefined
  return prender(valor, definicao.min, definicao.max)
}

/** Os parâmetros efetivos do objeto: o que ele guarda, ou o padrão, sempre dentro do intervalo. */
export function parametrosDoObjeto(objeto: Pick<ObjetoTabletop, 'tipo' | 'parametros'>): Record<string, number> {
  const efetivos: Record<string, number> = {}
  for (const d of definicoesDeParametros(objeto.tipo)) {
    const guardado = objeto.parametros?.[d.chave]
    efetivos[d.chave] = prender(typeof guardado === 'number' && Number.isFinite(guardado) ? guardado : d.padrao, d.min, d.max)
  }
  return efetivos
}
